import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Spinner,
  Card,
} from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { FiCheckCircle, FiAlertTriangle, FiMail } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const { t } = useTranslation();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage(t('auth.noVerificationToken'));
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await api.post('/api/auth/verify-email', { token });
        setStatus('success');
        setMessage(response.data.message || t('auth.emailVerifiedSuccessfully'));
      } catch (error: any) {
        setStatus('error');
        setMessage(
          error.response?.data?.detail ||
          t('auth.verificationFailedExpiredInvalid')
        );
      }
    };

    verifyEmail();
  }, [token, t]);

  return (
    <Box bg={bg} minH="100vh" py={20}>
      <Container maxW="md">
        <Card.Root bg={cardBg} p={8}>
          <VStack gap={6} textAlign="center">
            {status === 'loading' && (
              <>
                <Spinner size="xl" color="blue.500" />
                <Heading size="md">{t('auth.verifyingEmail')}</Heading>
                <Text color="gray.500">{t('auth.pleaseWaitVerifyEmail')}</Text>
              </>
            )}

            {status === 'success' && (
              <>
                <Box color="green.500" fontSize="5xl"><FiCheckCircle /></Box>
                <Heading size="md" color="green.600">{t('auth.emailVerifiedTitle')}</Heading>
                <Text color="gray.600">{message}</Text>
                <Button
                  colorPalette="brand"
                  size="lg"
                  onClick={() => navigate('/login')}
                  mt={4}
                >
                  {t('auth.goToLogin')}
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <Box color="red.500" fontSize="5xl"><FiAlertTriangle /></Box>
                <Heading size="md" color="red.600">{t('auth.verificationFailedTitle')}</Heading>
                <Text color="gray.600">{message}</Text>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  mt={4}
                >
                  {t('auth.backToLogin')}
                </Button>
              </>
            )}

            {status === 'no-token' && (
              <>
                <Box color="orange.500" fontSize="5xl"><FiMail /></Box>
                <Heading size="md">{t('auth.checkYourEmail')}</Heading>
                <Text color="gray.600">{message}</Text>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  mt={4}
                >
                  {t('auth.backToLogin')}
                </Button>
              </>
            )}
          </VStack>
        </Card.Root>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;
